import React, { useState, useContext, useEffect } from "react";
import { userContext } from "./../../UserContext";
import {
  Button,
  List,
  Result,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Card,
} from "antd";
import Spinner from "./../loading/Spinner";
import CoreUserCard from "../user/CoreUserCard";
import SsoConnectionsCard from "../user/SsoConnectionsCard";
import { Content, FeedbackBanner, Avatar } from "./../../library/src/";
import moment from "moment";
//hooks
import { useNotification } from "./../../Utils/Hooks/useNotification";
import { useFetch } from "../../Utils/Hooks/useFetch";
import { appContext } from "../../AppContext";
import { useTranslation } from "react-i18next";
import {
  CheckCircleOutlined,
  MailOutlined,
  ExclamationCircleOutlined,
  MobileOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import SaveButtonBar from "../../library/src/components/button/SaveButtonBar";

export default function User() {
  const [doFetch, FetchType] = useFetch();
  const [userState, userDispatch] = useContext(userContext);
  const userId = new URLSearchParams(window.location.search).get("id");
  const [currentUser, setCurrentUser] = useState(undefined);
  const [originalUser, setOriginalUser] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSelf, setIsSelf] = useState(true);
  const [createNotification, NotificationType] = useNotification();
  const [appState] = useContext(appContext);
  const { t } = useTranslation(["user", "utils"]);
  const [isUnlockLoading, setIsUnlockLoading] = useState(false);

  useEffect(
    () => {
      if (!isLoading) {
        return;
      }
      refresh();
    }, // eslint-disable-next-line
    [userState.isLoggedIn, isLoading, isSelf]
  );

  if (isLoading) {
    return <Spinner type="filled" />;
  }

  function refresh() {
    if (userState.isLoggedIn && isSelf) {
      doFetch("/user/currentWithPersonNumbers").then((response) => {
        if (response.isSuccess) {
          setIsSelf(response.result.id.toString() === userId);
          if (response.result.id.toString() === userId) {
            setCurrentUser(response.result);
            setOriginalUser(response.result);
            setIsLoading(false);
            return;
          }
          return;
        }

        userDispatch({ type: "LOGGEDOUT" });
      });
    }

    if (!isSelf) {
      doFetch(`/usermanagement/getuser/${userId}`).then((response) => {
        if (response.isSuccess) {
          setCurrentUser(response.result);
          setOriginalUser(response.result);
          setIsLoading(false);
          return;
        }

        setIsLoading(false);
      });
    }
  }

  function onClickSubmit() {
    doFetch(
      `/usermanagement/setUser/${userId}`,
      FetchType.POST,
      currentUser
    ).then((response) => {
      if (response.isSuccess) {
        createNotification(
          NotificationType.SUCCESS,
          t("notifications.userChangeSuccess.title"),
          t("notifications.userChangeSuccess.message")
        );

        setIsLoading(true);
        return;
      }

      createNotification(
        NotificationType.ERROR,
        t("notifications.userChangeFailure.title"),
        t("notifications.userChangeFailure.message")
      );
    });
  }

  if (currentUser === undefined) {
    return (
      <Result
        status="404"
        title={t("notifications.userNotFound.title")}
        subTitle={t("notifications.userNotFound.message")}
      />
    );
  }

  function verifyMailStatus() {
    return (
      <>
        {currentUser.emailValidated ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("user.emailVerified")}
          </Tag>
        ) : (
          <Tag icon={<ExclamationCircleOutlined />} color="warning">
            {t("user.emailNotVerified")}
          </Tag>
        )}
      </>
    );
  }

  function SavePermissions() {
    return (
      <Space
        className="status-save-container"
        size="large"
        key="3"
        style={{ flexGrow: 1, width: "100%" }}
      >
        <Space>
          <Button
            size="large"
            key="1"
            type="primary"
            disabled={
              JSON.stringify(originalUser) === JSON.stringify(currentUser)
            }
            onClick={onClickSubmit}
          >
            {t("utils:buttons.save")}
          </Button>
        </Space>
      </Space>
    );
  }

  function validateEmailButtons() {
    if (
      currentUser.emailValidated ||
      !appState.user.isAdministrator ||
      originalUser.email === null
    ) {
      return null;
    }

    return appState.hasSSO ? (
      <Button size="large" onClick={onClickSSOVerification}>
        {t("user.ssoVerification")}
      </Button>
    ) : (
      <Button size="large" onClick={onClickEmailVerification}>
        {t("user.emailVerification")}
      </Button>
    );
  }

  function onClickSSOVerification() {
    doFetch(
      `/Email/SendSSOValidationMail/${currentUser.id}`,
      FetchType.POST
    ).then((response) => {
      if (response.isSuccess) {
        createNotification(
          NotificationType.SUCCESS,
          t("notifications.sendSSOEmailSuccess.title"),
          t("notifications.sendSSOEmailSuccess.message")
        );

        setIsLoading(true);
        return;
      }

      createNotification(
        NotificationType.ERROR,
        t("notifications.sendSSOEmailFailure.title"),
        t("notifications.sendSSOEmailFailure.message")
      );
    });
  }

  function onClickEmailVerification() {
    doFetch(
      `/Email/SendEmailValidationMail/${currentUser.id}`,
      FetchType.POST
    ).then((response) => {
      if (response.isSuccess) {
        createNotification(
          NotificationType.SUCCESS,
          t("notifications.sendEmailSuccess.title"),
          t("notifications.sendEmailSuccess.message")
        );

        setIsLoading(true);
        return;
      }

      createNotification(
        NotificationType.ERROR,
        t("notifications.sendEmailFailure.title"),
        t("notifications.sendEmailFailure.message")
      );
    });
  }

  function UnlockUserButton() {
    if (!appState.user.isAdministrator) {
      return null;
    }

    return (
      <Button
        size="large"
        onClick={onClickUnlockUser}
        loading={isUnlockLoading}
      >
        {t("user.unlock")}
      </Button>
    );

    function onClickUnlockUser() {
      setIsUnlockLoading(true);
      doFetch(
        `/UserManagement/${currentUser.id}/unlockuser`,
        FetchType.POST
      ).then((response) => {
        setIsUnlockLoading(false);

        if (response.isSuccess) {
          createNotification(
            NotificationType.SUCCESS,
            t("notifications.unlockSuccess.title"),
            t("notifications.unlockSuccess.message")
          );
          refresh();

          return;
        }

        createNotification(
          NotificationType.ERROR,
          t("notifications.unlockFailure.title"),
          t("notifications.unlockFailure.message")
        );
      });
    }
  }

  const getContactIcon = {
    Email: <MailOutlined style={{ marginRight: "16px", fontSize: "18px" }} />,
    Cellphone: (
      <MobileOutlined style={{ marginRight: "16px", fontSize: "18px" }} />
    ),
    Telephone: (
      <PhoneOutlined style={{ marginRight: "16px", fontSize: "18px" }} />
    ),
  };

  const getContactHref = {
    Email: `mailto:${currentUser.person.personNumbers.userData}`,
    Cellphone: `tel:${currentUser.person.personNumbers.userData}`,
    Telephone: `tel:${currentUser.person.personNumbers.userData}`,
  };

  return (
    <>
      <SaveButtonBar
        show={
          JSON.stringify(originalUser) === JSON.stringify(currentUser)
            ? false
            : true
        }
        // show={false}
        onClickSave={onClickSubmit}
      />
      <Content>
        <div
          style={{
            marginLeft: "auto",
            marginRight: "auto",
            maxWidth: "1184px",
            width: "100%",
            paddingTop: 48,
          }}
        >
          <Row gutter={[24, 24]}>
            <Col flex="420px">
              <Card>
                <Card.Meta
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                  avatar={
                    <Avatar name={currentUser.person.summary} size={64} />
                  }
                  title={currentUser.person.summary}
                  description={appState.useEmail && verifyMailStatus()}
                />
                <List
                  header={
                    <Typography.Title level={5}>
                      {t("user.contact.title")}
                    </Typography.Title>
                  }
                  dataSource={currentUser.person.personNumbers}
                  renderItem={(item) => (
                    <List.Item
                      key={item.personNumbers}
                      style={{ justifyContent: "flex-start" }}
                    >
                      {getContactIcon[item.numberTypeName]}
                      <Typography.Link
                        href={getContactHref[item.numberTypeName]}
                      >
                        {item.userData}
                      </Typography.Link>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
            <Col flex="auto">
              {/* {appState.useEmail && !currentUser.emailValidated && (
                <FeedbackBanner
                  message={t("user:user.ssoVerificationFeedbackTitle")}
                  description={t(
                    "user:user.ssoVerificationFeedbackDescription"
                  )}
                  feedbackType="Warning"
                  action={validateEmailButtons()}
                  closable={false}
                />
              )} */}

              {currentUser.userStatusId === 4 &&
                currentUser.lastLockoutDate != null &&
                moment(currentUser.lastLockoutDate)
                  .add(appState.lockoutTime, "minutes")
                  .format("yyyy-MM-DD hh:mm") >
                  moment().format("yyyy-MM-DD hh:mm") && (
                  <FeedbackBanner
                    message={t("user:user.lockedFeedbackTitle")}
                    description={t("user:user.lockedFeedbackDescription")}
                    feedbackType="Warning"
                    closable={false}
                    action={UnlockUserButton()}
                  />
                )}

              <CoreUserCard
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
                isRefresh={setIsLoading}
                save={onClickSubmit}
              />

              {appState.hasSSO && (
                <SsoConnectionsCard
                  currentUser={currentUser}
                  isRefresh={setIsLoading}
                />
              )}
              <div style={{ marginBottom: 48, textAlign: "right" }}>
                <Button
                  type="primary"
                  size="large"
                  disabled={
                    JSON.stringify(originalUser) === JSON.stringify(currentUser)
                  }
                  onClick={onClickSubmit}
                  style={{ flexGrow: 0 }}
                >
                  {t("utils:buttons.save")}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Content>
    </>
  );
}
